import dayjs from "dayjs";
import type { VirtualClassroomDto } from "../types/client-education-detail.types";

/** Oturuma katılma penceresi — başlamadan kaç dk önce katılım açılır. */
const JOIN_WINDOW_MS = 15 * 60_000;

interface ClassroomWindow {
  start: number;
  end: number;
}

function extractWindows(vc: VirtualClassroomDto): ClassroomWindow[] {
  const out: ClassroomWindow[] = [];
  const times = vc.scheduledTimes ?? [];
  if (times.length) {
    for (const t of times) {
      const start = dayjs(`${t.date}T${t.startTime ?? "00:00"}`);
      const end = dayjs(`${t.date}T${t.endTime ?? "23:59"}`);
      if (!start.isValid() || !end.isValid()) continue;
      out.push({ start: start.valueOf(), end: end.valueOf() });
    }
    return out;
  }
  // scheduledTimes yoksa sınıfın kendi tarih aralığına düş. Tek uçlu aralıklar da
  // geçerli sayılır: eskiden yalnız start VE end birlikte doluysa pencere üretiliyordu,
  // dolayısıyla sadece başlangıcı (ya da sadece bitişi) girilmiş sınıflarda "Katıl"
  // butonu hiç görünmüyordu — UAT'de "Jitsi katılım butonu bazen görünmüyor" bulgusu.
  if (vc.startDate || vc.endDate) {
    const start = vc.startDate ? dayjs(vc.startDate) : null;
    const end = vc.endDate ? dayjs(vc.endDate) : null;
    const startMs = start?.isValid() ? start.valueOf() : null;
    const endMs = end?.isValid() ? end.valueOf() : null;

    if (startMs != null || endMs != null) {
      out.push({
        // Yalnız bitiş varsa: bitişe kadar açık kabul et.
        start: startMs ?? Number.NEGATIVE_INFINITY,
        // Yalnız başlangıç varsa: o günün sonuna kadar açık kabul et. Sınırsız
        // bırakmak, tarihi geçmiş sınıfları sonsuza dek "katılınabilir" gösterirdi.
        end: endMs ?? (startMs != null ? dayjs(startMs).endOf("day").valueOf() : 0),
      });
    }
  }
  return out;
}

export interface JitsiAvailability {
  /** Oturum başladıysa veya 15 dk içinde başlayacaksa true. */
  canJoin: boolean;
  /** İleride bir oturum varsa onun başlangıç zamanı. */
  nextStart: number | null;
}

export function getJitsiAvailability(
  classrooms: VirtualClassroomDto[] | undefined | null,
  now: Date = new Date(),
): JitsiAvailability {
  const nowMs = now.getTime();
  let canJoin = false;
  let nextStart: number | null = null;

  for (const vc of classrooms ?? []) {
    for (const { start, end } of extractWindows(vc)) {
      if (nowMs >= start - JOIN_WINDOW_MS && nowMs <= end) {
        canJoin = true;
      } else if (nowMs < start) {
        if (nextStart == null || start < nextStart) nextStart = start;
      }
    }
  }

  return { canJoin, nextStart };
}
